import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, XIcon, PlusCircleIcon, CarIcon, UserIcon, PhoneIcon, HashIcon, ZapIcon, TicketIcon } from 'lucide-react';
interface Vehicle {
  id: string;
  ticketNumber: string;
  name: string;
  phone: string;
  plateNumber: string;
  vehicleType: string;
  category: string;
  pumpNumber: number;
  serviceType: string;
  estimatedPrice: number;
  addedAt: Date;
  status: 'waiting' | 'in-service' | 'completed';
  addedBy: string;
  addedByName: string;
}
interface QuickAccessModalProps {
  onClose: () => void;
  queue: Vehicle[];
  maxPumps: number;
  existingPlateNumbers: string[];
  onAdd: (vehicle: Omit<Vehicle, 'id' | 'addedAt' | 'status' | 'ticketNumber' | 'addedBy' | 'addedByName'>) => void;
  onNavigateToVehicle: (vehicleId: string) => void;
  canAdd: boolean;
}
export function QuickAccessModal({
  onClose,
  queue,
  maxPumps,
  existingPlateNumbers,
  onAdd,
  onNavigateToVehicle,
  canAdd
}: QuickAccessModalProps) {
  const [activeTab, setActiveTab] = useState<'search' | 'add'>('search');
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Quick Add Form State
  const [plateNumber, setPlateNumber] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [estimatedPrice, setEstimatedPrice] = useState('');
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [activeTab]);
  const searchResults = queue.filter(vehicle => {
    if (!searchTerm.trim()) return false;
    const search = searchTerm.toLowerCase();
    const position = (queue.indexOf(vehicle) + 1).toString();
    return vehicle.plateNumber.toLowerCase().includes(search) || vehicle.ticketNumber.toLowerCase().includes(search) || vehicle.name.toLowerCase().includes(search) || vehicle.phone.includes(search) || position.includes(search);
  });
  const handleQuickAdd = () => {
    if (!canAdd) {
      alert("Vous n'avez pas la permission d'ajouter des véhicules à cette station");
      return;
    }
    if (!plateNumber.trim()) {
      alert("Le numéro d'immatriculation est requis");
      return;
    }
    if (existingPlateNumbers.includes(plateNumber.trim().toUpperCase())) {
      alert("Ce numéro d'immatriculation est déjà dans la file");
      return;
    }
    onAdd({
      plateNumber: plateNumber.trim().toUpperCase(),
      name: name.trim() || 'Client',
      phone: phone.trim() || 'Non renseigné',
      pumpNumber: 1,
      vehicleType: 'Voiture',
      category: 'Particulier',
      serviceType: 'Plein',
      estimatedPrice: estimatedPrice ? Number(estimatedPrice) : 15000
    });
    setPlateNumber('');
    setName('');
    setPhone('');
    setEstimatedPrice('');
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && activeTab === 'add') {
      e.preventDefault();
      handleQuickAdd();
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-449">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden" data-id="element-450">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white" data-id="element-451">
          <div className="flex items-center justify-between mb-4" data-id="element-452">
            <div className="flex items-center gap-3" data-id="element-453">
              <ZapIcon className="w-8 h-8" data-id="element-454" />
              <h2 className="text-2xl font-bold" data-id="element-455">Accès Rapide</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors" data-id="element-456">
              <XIcon className="w-6 h-6" data-id="element-457" />
            </button>
          </div>
          {/* Tabs */}
          <div className="flex gap-2" data-id="element-458">
            <button onClick={() => setActiveTab('search')} className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${activeTab === 'search' ? 'bg-white text-blue-600' : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'}`} data-id="element-459">
              <SearchIcon className="w-5 h-5" data-id="element-460" />
              Recherche Rapide
            </button>
            {canAdd && <button onClick={() => setActiveTab('add')} className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${activeTab === 'add' ? 'bg-white text-blue-600' : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'}`} data-id="element-461">
                <PlusCircleIcon className="w-5 h-5" data-id="element-462" />
                Saisie Express
              </button>}
          </div>
        </div>
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]" data-id="element-463">
          {activeTab === 'search' ? <div data-id="element-464">
              {/* Search Bar */}
              <div className="mb-6" data-id="element-465">
                <div className="relative" data-id="element-466">
                  <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" data-id="element-467" />
                  <input ref={searchInputRef} type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Rechercher par ticket, immatriculation, nom, téléphone ou n° de rang..." className="w-full pl-14 pr-4 py-4 text-lg border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors" data-id="element-468" />
                </div>
                <p className="text-sm text-gray-500 mt-2" data-id="element-469">
                  Tapez pour rechercher dans la file d'attente actuelle
                </p>
              </div>
              {/* Search Results */}
              {searchTerm.trim() ? <div data-id="element-470">
                  <div className="flex items-center justify-between mb-4" data-id="element-471">
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-472">
                      Résultats de recherche
                    </h3>
                    <span className="text-sm text-gray-600" data-id="element-473">
                      {searchResults.length} résultat
                      {searchResults.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {searchResults.length === 0 ? <div className="text-center py-12 bg-gray-50 rounded-xl" data-id="element-474">
                      <SearchIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-475" />
                      <p className="text-gray-500 text-lg" data-id="element-476">
                        Aucun résultat trouvé
                      </p>
                      <p className="text-gray-400 text-sm mt-2" data-id="element-477">
                        Essayez une autre recherche
                      </p>
                    </div> : <div className="space-y-3" data-id="element-478">
                      {searchResults.map((vehicle, index) => {
                const position = queue.indexOf(vehicle) + 1;
                return <button key={vehicle.id} onClick={() => {
                  onNavigateToVehicle(vehicle.id);
                  onClose();
                }} className="w-full text-left bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4 hover:shadow-lg transition-all" data-id="element-479">
                            <div className="flex items-center gap-4" data-id="element-480">
                              <div className="bg-blue-600 text-white rounded-lg px-4 py-2 font-bold text-xl" data-id="element-481">
                                #{position}
                              </div>
                              <div className="flex-1" data-id="element-482">
                                <div className="flex items-center gap-2 mb-2" data-id="element-483">
                                  <div className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-3 py-1 rounded-lg" data-id="element-484">
                                    <TicketIcon className="w-4 h-4" data-id="element-485" />
                                    <span className="font-bold text-sm" data-id="element-486">
                                      {vehicle.ticketNumber}
                                    </span>
                                  </div>
                                  <CarIcon className="w-5 h-5 text-blue-600" data-id="element-487" />
                                  <span className="font-bold text-lg text-gray-900" data-id="element-488">
                                    {vehicle.plateNumber}
                                  </span>
                                  <span className="text-sm px-2 py-1 bg-white rounded-md text-gray-700" data-id="element-489">
                                    Pompe {vehicle.pumpNumber}
                                  </span>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-gray-700" data-id="element-490">
                                  <div className="flex items-center gap-1" data-id="element-491">
                                    <UserIcon className="w-4 h-4" data-id="element-492" />
                                    <span data-id="element-493">{vehicle.name}</span>
                                  </div>
                                  <span data-id="element-494">•</span>
                                  <div className="flex items-center gap-1" data-id="element-495">
                                    <PhoneIcon className="w-4 h-4" data-id="element-496" />
                                    <span data-id="element-497">{vehicle.phone}</span>
                                  </div>
                                </div>
                              </div>
                              <div className={`px-4 py-2 rounded-lg font-semibold ${vehicle.status === 'waiting' ? 'bg-yellow-100 text-yellow-800' : vehicle.status === 'in-service' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-498">
                                {vehicle.status === 'waiting' ? 'En Attente' : vehicle.status === 'in-service' ? 'En Service' : 'Terminé'}
                              </div>
                            </div>
                          </button>;
              })}
                    </div>}
                </div> : <div className="text-center py-12 bg-gray-50 rounded-xl" data-id="element-499">
                  <SearchIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-500" />
                  <p className="text-gray-500 text-lg" data-id="element-501">
                    Commencez à taper pour rechercher
                  </p>
                  <p className="text-gray-400 text-sm mt-2" data-id="element-502">
                    Recherchez par ticket, immatriculation, nom, téléphone ou
                    numéro de rang
                  </p>
                </div>}
            </div> : <div data-id="element-503">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4 mb-6" data-id="element-504">
                <h3 className="font-bold text-blue-900 mb-2" data-id="element-505">
                  ⚡ Saisie Express
                </h3>
                <p className="text-sm text-blue-700" data-id="element-506">
                  Remplissez rapidement les informations essentielles. Les
                  détails peuvent être complétés plus tard.
                </p>
              </div>
              <div className="space-y-4" onKeyPress={handleKeyPress} data-id="element-507">
                {/* Plate Number */}
                <div data-id="element-508">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-509">
                    <div className="flex items-center gap-2" data-id="element-510">
                      <CarIcon className="w-4 h-4" data-id="element-511" />
                      Immatriculation *
                    </div>
                  </label>
                  <input type="text" value={plateNumber} onChange={e => setPlateNumber(e.target.value.toUpperCase())} placeholder="Ex: ML-1234-AB" className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors text-lg font-semibold" data-id="element-512" />
                </div>
                {/* Name */}
                <div data-id="element-513">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-514">
                    <div className="flex items-center gap-2" data-id="element-515">
                      <UserIcon className="w-4 h-4" data-id="element-516" />
                      Nom du Client
                    </div>
                  </label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Amadou Diarra" className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors" data-id="element-517" />
                </div>
                {/* Phone */}
                <div data-id="element-518">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-519">
                    <div className="flex items-center gap-2" data-id="element-520">
                      <PhoneIcon className="w-4 h-4" data-id="element-521" />
                      Téléphone
                    </div>
                  </label>
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Ex: +223 76 12 34 56" className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors" data-id="element-522" />
                </div>
                {/* Estimated Price */}
                <div data-id="element-523">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-524">
                    <div className="flex items-center gap-2" data-id="element-525">
                      <HashIcon className="w-4 h-4" data-id="element-526" />
                      Prix Estimatif (FCFA)
                    </div>
                  </label>
                  <input type="number" value={estimatedPrice} onChange={e => setEstimatedPrice(e.target.value)} placeholder="Ex: 15000" min="0" step="500" className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors" data-id="element-527" />
                </div>
                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-id="element-528">
                  <p className="text-sm text-blue-800" data-id="element-529">
                    <strong data-id="element-530">💡 Astuce:</strong> Appuyez sur Entrée pour ajouter
                    rapidement. Les valeurs par défaut seront utilisées pour les
                    autres champs.
                  </p>
                </div>
                {/* Action Buttons */}
                <div className="flex gap-3 pt-4" data-id="element-531">
                  <button onClick={handleQuickAdd} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 rounded-xl font-bold text-lg transition-colors shadow-lg hover:shadow-xl" data-id="element-532">
                    <div className="flex items-center justify-center gap-2" data-id="element-533">
                      <PlusCircleIcon className="w-6 h-6" data-id="element-534" />
                      Ajouter Rapidement
                    </div>
                  </button>
                  <button onClick={() => {
                setPlateNumber('');
                setName('');
                setPhone('');
                setEstimatedPrice('');
              }} className="px-6 py-4 border-2 border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold transition-colors" data-id="element-535">
                    Réinitialiser
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}